import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEOHead from "../../components/shared/SEOHead.jsx";
import JsonLd from "../../components/shared/JsonLd.jsx";
import { buildBreadcrumbJsonLd, buildCollectionPageJsonLd } from "../../utils/jsonld.js";
import NewsletterEmbed from "../../components/newsletter/NewsletterEmbed.jsx";
import { larpPages, excerpt, estimateReadingTime, getReadableText, getFeaturedLarpPages } from "../../utils/content.js";

const LARP_PATH = { de: "larp-erleben", en: "experience-larp" };

export default function LarpHub({ lang }) {
  const { t } = useTranslation();
  const [activeTopic, setActiveTopic] = useState(null);
  const pages = larpPages.filter((p) => p.lang === lang);
  // Featured = `featured: true` in frontmatter, most recently created wins
  // if several are flagged — same rule as the homepage teaser
  // (getFeaturedLarpPages), reused here with limit 1. Falls back to the
  // first page (by file order) if nothing is flagged, so the hero never
  // just disappears on a fresh content set.
  const featured = getFeaturedLarpPages(lang, 1)[0] ?? pages[0];
  const rest = pages.filter((p) => p.frontmatter.id !== featured?.frontmatter.id);
  const topics = [...new Set(rest.map((p) => p.frontmatter.topic).filter(Boolean))].sort();
  const visiblePages = activeTopic ? rest.filter((p) => p.frontmatter.topic === activeTopic) : rest;

  return (
    <>
      <SEOHead
        lang={lang}
        path={`/${LARP_PATH[lang]}`}
        altPath={`/${LARP_PATH[lang === "de" ? "en" : "de"]}`}
        title={t("nav.larpErleben")}
        description={t("larp.body")}
      />
      <JsonLd
        data={buildBreadcrumbJsonLd(
          [
            { name: t("nav.home"), path: "/" },
            { name: t("nav.larpErleben"), path: `/${LARP_PATH[lang]}` },
          ],
          lang
        )}
      />
      <JsonLd
        data={buildCollectionPageJsonLd(
          {
            name: t("larp.title"),
            description: t("larp.body"),
            url: `https://talesandlegends.org/${lang}/${LARP_PATH[lang]}`,
            items: pages.map((p) => ({
              name: p.frontmatter.title,
              url: `https://talesandlegends.org/${lang}/${LARP_PATH[lang]}/${p.frontmatter.slug}`,
            })),
          },
          lang
        )}
      />
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div className="tl-container" style={{ display: "flex", flexDirection: "column", gap: "48px", padding: "64px 40px 96px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "20px", maxWidth: "56ch" }}>
            <p className="tl-eyebrow" style={{ margin: 0 }}>
              {t("larp.eyebrow")}
            </p>
            <h1 className="tl-hero-h1" style={{ fontFamily: "var(--font-serif)", fontSize: "44px", lineHeight: 1.1, color: "var(--color-cream)", margin: 0 }}>
              {t("larp.title")}
            </h1>
            <p style={{ fontSize: "16px", lineHeight: 1.7, color: "var(--color-cream-soft)", margin: 0 }}>{t("larp.body")}</p>
          </div>

          {featured && (
            <Link
              to={`/${lang}/${LARP_PATH[lang]}/${featured.frontmatter.slug}`}
              className="tl-card-light tl-hover-lift tl-grid-2"
              style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr" }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "40px" }}>
                <span style={{ fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "14px", color: "#8a6520" }}>
                  {featured.frontmatter.topic} · {t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(featured)) })} · {featured.frontmatter.author}
                </span>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "32px", lineHeight: 1.15, color: "var(--color-ink)", maxWidth: "26ch" }}>
                  {featured.frontmatter.title}
                </span>
                <p style={{ fontSize: "15px", lineHeight: 1.7, color: "rgba(10,9,7,0.7)", maxWidth: "50ch", margin: 0 }}>
                  {featured.frontmatter.subtitle || excerpt(featured.content)}
                </p>
                <span className="tl-pill tl-pill-primary" style={{ alignSelf: "flex-start", padding: "12px 24px", marginTop: "8px" }}>
                  {t("larp.readArticle")}
                </span>
              </div>
              <div className="tl-diagonal-light" style={{ minHeight: "260px" }} />
            </Link>
          )}

          {topics.length > 0 && (
            <div role="group" aria-label={t("larp.filterByTopic")} style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => setActiveTopic(null)}
                className={`tl-pill ${activeTopic === null ? "tl-pill-primary" : "tl-pill-outline"}`}
                style={{ padding: "9px 18px", fontSize: "13px" }}
              >
                {t("larp.allTopics")}
              </button>
              {topics.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => setActiveTopic(topic)}
                  aria-pressed={activeTopic === topic}
                  className={`tl-pill ${activeTopic === topic ? "tl-pill-primary" : "tl-pill-outline"}`}
                  style={{ padding: "9px 18px", fontSize: "13px" }}
                >
                  {topic}
                </button>
              ))}
            </div>
          )}

          {rest.length > 0 && visiblePages.length === 0 && (
            <p style={{ color: "var(--color-cream-soft)" }}>{t("larp.noTopicResults")}</p>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "24px" }}>
            {visiblePages.map((page) => (
              <Link
                key={page.frontmatter.id}
                to={`/${lang}/${LARP_PATH[lang]}/${page.frontmatter.slug}`}
                className="tl-card tl-hover-raise"
                style={{ display: "flex", flexDirection: "column", gap: "12px", minHeight: "240px" }}
              >
                <span className="tl-eyebrow" style={{ fontSize: "12px" }}>
                  {page.frontmatter.topic} · {t("larp.readingTime", { minutes: estimateReadingTime(getReadableText(page)) })}
                </span>
                <span style={{ fontFamily: "var(--font-serif)", fontSize: "21px", lineHeight: 1.25, color: "var(--color-cream)" }}>
                  {page.frontmatter.title}
                </span>
                <span style={{ fontSize: "14px", lineHeight: 1.65, color: "var(--color-cream-soft)" }}>
                  {page.frontmatter.subtitle || excerpt(page.content)}
                </span>
                <span style={{ fontSize: "13px", color: "var(--color-cream)", marginTop: "4px" }}>{t("larp.readMore")}</span>
              </Link>
            ))}

            <div className="tl-card-light" style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "28px", minHeight: "240px", gridColumn: "-2", gridRow: "1" }}>
              <span style={{ fontFamily: "var(--font-serif)", fontSize: "21px", lineHeight: 1.25, color: "var(--color-ink)" }}>
                {t("nav.newsletter")}
              </span>
              <div style={{ marginTop: "4px" }}>
                <NewsletterEmbed lang={lang} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
